import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const HERO_SHOWCASES = [
  {
    id: 'commercials',
    tabLabel: '🎬 3D Commercials',
    badge: 'Broadcast & Social Ready',
    title: 'Lumina Citrus & Culinary 3D Spots',
    subtitle: '4K 60fps fluid simulations, product commercials & brand customization packages.',
    image: '/images/products/drink_commercial.jpg',
    link: '/products',
    ctaText: 'Browse Commercials & 3D Files →'
  },
  {
    id: 'animation',
    tabLabel: '✨ Studio Animation',
    badge: '2D & 3D Production',
    title: 'Silver, Gold & Platinum Animation',
    subtitle: 'Full-cycle storyboarding, character animation, VFX, and sonic branding tailored to your campaign.',
    image: '/images/products/courtroom_commercial.jpg',
    link: '/animation',
    ctaText: 'View Animation Packages →'
  },
  {
    id: 'paintings',
    tabLabel: '🖼️ Fine Art',
    badge: 'Original Canvas & Digital',
    title: 'Curated Fine Art & Paintings Gallery',
    subtitle: 'Collect statement pieces, contemporary visual art, and limited-edition gallery works.',
    image: '/images/paintings/painting1.png',
    link: '/paintings',
    ctaText: 'Enter Art Gallery →'
  },
  {
    id: '3d-models',
    tabLabel: '📦 3D Assets',
    badge: 'Unreal / Blender / Cinema4D',
    title: 'Studio-Grade 3D Meshes & Environments',
    subtitle: 'PBR-textured 4K/8K assets ready to drop into games, virtual production, and broadcast.',
    image: '/images/products/asset_cocktail_3d.jpg',
    link: '/products',
    ctaText: 'Explore 3D Asset Vault →'
  }
];

const ECOSYSTEM_DIVISIONS = [
  {
    id: 'animation',
    title: 'Animation Studio',
    category: 'production',
    categoryLabel: 'Production & 3D',
    icon: '🎬',
    featuredWide: true,
    accent: 'linear-gradient(90deg, #00ffff, #d946ef)',
    description: 'Bring your brand vision to life with custom 2D & 3D animation, motion graphics, and cinematic commercial production in Silver, Gold, and Platinum tiers.',
    tags: ['2D & 3D Animation', 'Storyboarding', 'Broadcast VFX', 'Custom Quotes'],
    to: '/animation',
    cta: 'Explore Packages & Pricing'
  },
  {
    id: 'products',
    title: 'Products & 3D Assets',
    category: 'production',
    categoryLabel: 'Production & 3D',
    icon: '📦',
    featuredWide: true,
    accent: 'linear-gradient(90deg, #d946ef, #f59e0b)',
    description: 'Ready-to-brand 30-second broadcast commercials (Beverage, Culinary, Law Firm) and downloadable 3D source models with PBR textures.',
    tags: ['30s Commercials', 'FBX / OBJ / Blend', '4K UHD', 'Turnkey Branding'],
    to: '/products',
    cta: 'Shop Commercials & 3D Files'
  },
  {
    id: 'paintings',
    title: 'Fine Art & Paintings',
    category: 'art',
    categoryLabel: 'Art & Lifestyle',
    icon: '🖼️',
    accent: 'linear-gradient(90deg, #f59e0b, #ec4899)',
    description: 'Explore original paintings, contemporary canvas creations, and expressive visual storytelling crafted by ThrillArts artists.',
    tags: ['Original Canvas', 'Gallery Prints', 'Collector Pieces'],
    to: '/paintings',
    cta: 'View Paintings Gallery'
  },
  {
    id: 'games',
    title: 'Games & Interactive',
    category: 'interactive',
    categoryLabel: 'Interactive & Tech',
    icon: '🎮',
    accent: 'linear-gradient(90deg, #00ffff, #3b82f6)',
    description: 'High-energy interactive experiences, original indie game titles, and immersive digital worlds built for modern players.',
    tags: ['Interactive Play', '3D Worlds', 'Arcade & Indie'],
    to: '/games',
    cta: 'Launch Games Hub'
  },
  {
    id: 'virtual-tours',
    title: 'Virtual Tours & Spaces',
    category: 'interactive',
    categoryLabel: 'Interactive & Tech',
    icon: '🏠',
    accent: 'linear-gradient(90deg, #10b981, #00ffff)',
    description: 'Step inside interactive 360° architectural walkthroughs, digital showrooms, and spatial web environments.',
    tags: ['360° Walkthroughs', 'Digital Showrooms', 'Spatial Web'],
    to: '/virtual-tours',
    cta: 'Experience Virtual Tours'
  },
  {
    id: 'fashion',
    title: 'Fashion & Couture',
    category: 'art',
    categoryLabel: 'Art & Lifestyle',
    icon: '👗',
    accent: 'linear-gradient(90deg, #ec4899, #8b5cf6)',
    description: 'Bold wearable art, signature apparel collections, and futuristic streetwear blending studio aesthetics with everyday style.',
    tags: ['Wearable Art', 'Streetwear', 'Lookbook'],
    to: '/fashion',
    cta: 'Browse Fashion Line'
  },
  {
    id: 'ai',
    title: 'ThrillArts A.I.',
    category: 'interactive',
    categoryLabel: 'Interactive & Tech',
    icon: '🤖',
    accent: 'linear-gradient(90deg, #06b6d4, #a855f7)',
    description: 'Next-generation generative AI creative tools, intelligent concept pipelines, and automated visual design systems.',
    tags: ['Generative AI', 'Smart Workflows', 'Neural Art'],
    href: 'https://ThrillArtsAI.com',
    cta: 'Visit ThrillArtsAI.com ↗'
  },
  {
    id: 'music',
    title: 'ThrillArts Music',
    category: 'media',
    categoryLabel: 'Music & Social',
    icon: '🎵',
    accent: 'linear-gradient(90deg, #8b5cf6, #00ffff)',
    description: 'Original music production, sonic branding, licensing tracks, and immersive audioscapes for creators and brands.',
    tags: ['Original Tracks', 'Sonic Branding', 'Audio Licensing'],
    href: 'https://thrillartsMusic.com',
    cta: 'Listen on ThrillArtsMusic ↗'
  },
  {
    id: 'tattoos',
    title: 'ThrillArts Tattoos',
    category: 'art',
    categoryLabel: 'Art & Lifestyle',
    icon: '🎨',
    accent: 'linear-gradient(90deg, #f97316, #ef4444)',
    description: 'Custom tattoo design, bespoke body art concepts, and flash collections crafted by master illustrators.',
    tags: ['Custom Ink', 'Body Art', 'Studio Flash'],
    href: 'https://thrillartsTattoos.com/',
    cta: 'Explore Tattoo Studio ↗'
  },
  {
    id: 'social',
    title: 'ThrillArts Social',
    category: 'media',
    categoryLabel: 'Music & Social',
    icon: '🌐',
    accent: 'linear-gradient(90deg, #3b82f6, #00ffff)',
    description: 'Connect with artists, developers, and collectors across the ThrillArts Post community network.',
    tags: ['Creator Network', 'Community Feed', 'Collaborations'],
    href: 'https://thrillartspost.com/',
    cta: 'Join Social Network ↗'
  }
];

const FEATURED_SPOTLIGHTS = [
  {
    title: 'Lumina Citrus Splash — 30s Spot',
    category: 'Food & Drinks Commercial',
    price: '$1,450',
    image: '/images/products/drink_commercial.jpg',
    description: 'High-octane 3D fluid dynamics & ice splash broadcast commercial with full brand customization included.',
    link: '/products'
  },
  {
    title: 'Artisan Flame Burger — 30s Spot',
    category: 'Culinary CGI Commercial',
    price: '$1,650',
    image: '/images/products/food_commercial.jpg',
    description: 'Slow-motion 3D culinary spot with sizzling ASMR sound design and vertical social media cutdowns.',
    link: '/products'
  },
  {
    title: 'Apex Legal Partners — 30s Spot',
    category: 'Law Firm Broadcast Spot',
    price: '$1,850',
    image: '/images/products/lawyer_commercial.jpg',
    description: 'Executive broadcast commercial engineered for trial lawyers, corporate counsel, and litigation firms.',
    link: '/products'
  }
];

function Home() {
  const [activeShowcase, setActiveShowcase] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const currentHeroItem = HERO_SHOWCASES[activeShowcase];

  const filteredDivisions = ECOSYSTEM_DIVISIONS.filter((item) => {
    const matchesCat = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesQuery =
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tags.some((t) => t.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCat && matchesQuery;
  });

  return (
    <div className="home-page">
      <div className="home-grid-backdrop" aria-hidden="true" />

      {/* Top Sticky Navbar */}
      <header className="home-navbar">
        <div className="home-navbar-inner">
          <Link to="/" className="home-brand">
            <div className="brand-mark">T</div>
            <div className="brand-text-wrap">
              <span className="brand-name">ThrillArts</span>
              <span className="brand-tagline">Creative Universe</span>
            </div>
          </Link>

          <nav className="home-nav-links" aria-label="Primary Navigation">
            <a href="#ecosystem">Ecosystem Hub</a>
            <Link to="/animation">Animation Studio</Link>
            <Link to="/products">Products & 3D</Link>
            <Link to="/paintings">Fine Art</Link>
            <Link to="/games">Games</Link>
            <a href="#services">Talent & Services</a>
          </nav>

          <div className="home-nav-cta">
            <Link to="/products" className="nav-btn-ghost">
              3D Marketplace
            </Link>
            <Link to="/animation-quote" className="nav-btn-primary">
              Get a Quote ✨
            </Link>
          </div>
        </div>
      </header>

      <main className="home-main">
        {/* Attention-Grabbing Hero Section */}
        <section className="home-hero">
          <div className="hero-copy-col">
            <div className="hero-kicker">
              <span className="kicker-dot" />
              <span>Next-Gen Digital Art, 3D Production & Interactive Media</span>
            </div>

            <h1 className="hero-headline">
              One Creative Universe.{' '}
              <span className="hero-gradient-span">Infinite Visual Impact.</span>
            </h1>

            <p className="hero-lead">
              From broadcast-ready 3D commercials and custom animation packages to fine art,
              interactive games, virtual spaces, and AI innovation—everything at ThrillArts is
              organized and engineered to command attention.
            </p>

            <div className="hero-cta-row">
              <Link to="/products" className="btn-hero-main">
                Explore Commercials & 3D Assets →
              </Link>
              <Link to="/animation" className="btn-hero-secondary">
                🎬 Animation Packages
              </Link>
            </div>

            <div className="hero-stats-bar">
              <div className="hero-stat-item">
                <span className="stat-number">10+</span>
                <span className="stat-label">Specialized Creative Divisions</span>
              </div>
              <div className="hero-stat-item">
                <span className="stat-number">4K / 60fps</span>
                <span className="stat-label">Broadcast & Cinema Standard</span>
              </div>
              <div className="hero-stat-item">
                <span className="stat-number">2D & 3D</span>
                <span className="stat-label">Turnkey Custom Production</span>
              </div>
            </div>
          </div>

          {/* Interactive Live Preview Stage */}
          <div className="hero-showcase-stage">
            <div className="showcase-media-frame">
              <img
                src={currentHeroItem.image}
                alt={currentHeroItem.title}
                className="showcase-img"
              />
              <div className="showcase-overlay-gradient">
                <span className="showcase-tag">{currentHeroItem.badge}</span>
                <h2 className="showcase-title">{currentHeroItem.title}</h2>
                <p className="showcase-sub">{currentHeroItem.subtitle}</p>
                <Link to={currentHeroItem.link} className="showcase-link">
                  {currentHeroItem.ctaText}
                </Link>
              </div>
            </div>

            <div className="showcase-tabs" role="tablist" aria-label="Featured Studio Highlights">
              {HERO_SHOWCASES.map((item, idx) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={activeShowcase === idx}
                  className={`showcase-tab-btn ${activeShowcase === idx ? 'active' : ''}`}
                  onClick={() => setActiveShowcase(idx)}
                >
                  {item.tabLabel}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Organized Ecosystem Hub (Replaces Floating Icons) */}
        <section id="ecosystem" className="ecosystem-section">
          <div className="section-header-bar">
            <div>
              <span className="section-eyebrow">Organized Directory</span>
              <h2 className="section-title">Explore the ThrillArts Ecosystem</h2>
              <p className="section-subtitle">
                Every creative division, studio service, and digital platform—neatly organized so you
                can jump straight to what you need.
              </p>
            </div>
          </div>

          <div className="ecosystem-controls">
            <div className="category-pills" role="group" aria-label="Filter Divisions by Category">
              <button
                type="button"
                className={`category-pill-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('all')}
              >
                All Divisions ({ECOSYSTEM_DIVISIONS.length})
              </button>
              <button
                type="button"
                className={`category-pill-btn ${selectedCategory === 'production' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('production')}
              >
                🎬 Production & 3D
              </button>
              <button
                type="button"
                className={`category-pill-btn ${selectedCategory === 'art' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('art')}
              >
                🎨 Art & Lifestyle
              </button>
              <button
                type="button"
                className={`category-pill-btn ${selectedCategory === 'interactive' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('interactive')}
              >
                🎮 Interactive & Tech
              </button>
              <button
                type="button"
                className={`category-pill-btn ${selectedCategory === 'media' ? 'active' : ''}`}
                onClick={() => setSelectedCategory('media')}
              >
                🎵 Music & Social
              </button>
            </div>

            <div className="ecosystem-search-box">
              <input
                type="text"
                className="ecosystem-search-input"
                placeholder="Search divisions, 3D, art, AI..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                aria-label="Search ThrillArts divisions"
              />
            </div>
          </div>

          <div className="ecosystem-grid">
            {filteredDivisions.map((div) => {
              const CardWrapper = div.to ? Link : 'a';
              const wrapperProps = div.to
                ? { to: div.to }
                : { href: div.href, target: '_blank', rel: 'noopener noreferrer' };

              return (
                <CardWrapper
                  key={div.id}
                  {...wrapperProps}
                  className={`division-card ${
                    div.featuredWide && selectedCategory === 'all' && !searchTerm
                      ? 'featured-wide'
                      : ''
                  }`}
                  style={{ '--card-accent': div.accent }}
                >
                  <div>
                    <div className="division-card-top">
                      <div className="division-icon-box" aria-hidden="true">
                        {div.icon}
                      </div>
                      <span className="division-badge">{div.categoryLabel}</span>
                    </div>

                    <h3 className="division-title">{div.title}</h3>
                    <p className="division-desc">{div.description}</p>

                    <div className="division-tags">
                      {div.tags.map((tag) => (
                        <span key={tag} className="division-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="division-footer">
                    <span>{div.cta}</span>
                    <span className="division-arrow">→</span>
                  </div>
                </CardWrapper>
              );
            })}
          </div>
        </section>

        {/* Featured Turnkey Commercials & Assets */}
        <section className="featured-strip-section">
          <div className="section-header-bar">
            <div>
              <span className="section-eyebrow">Ready to Brand & Launch</span>
              <h2 className="section-title">Featured 30s Commercials & 3D Productions</h2>
              <p className="section-subtitle">
                Customize broadcast-ready commercials with your logo, product packaging, and call to
                action—or license raw 3D studio files.
              </p>
            </div>
            <Link to="/products" className="nav-btn-ghost">
              View All Products & 3D Assets →
            </Link>
          </div>

          <div className="featured-cards-grid">
            {FEATURED_SPOTLIGHTS.map((item) => (
              <Link to={item.link} key={item.title} className="spotlight-card">
                <div className="spotlight-img-wrap">
                  <img src={item.image} alt={item.title} />
                  <span className="spotlight-pill">{item.category}</span>
                  <span className="spotlight-price">{item.price}</span>
                </div>
                <div className="spotlight-body">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <span className="spotlight-cta">Inspect Specs & Customize →</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Talent, Auctions & Metaverse Services Portal */}
        <section id="services" className="services-portal-section">
          <div className="section-header-bar">
            <div>
              <span className="section-eyebrow">Collaborate & Scale</span>
              <h2 className="section-title">Hire Talent, Bid & Partner with ThrillArts</h2>
              <p className="section-subtitle">
                Direct access to our creative workforce, live art auctions, metaverse spaces, and
                franchise opportunities.
              </p>
            </div>
          </div>

          <div className="services-portal-grid">
            <Link to="/hire-artist" className="portal-tile">
              <span className="portal-tile-icon">🖌️</span>
              <h3>Hire an Artist</h3>
              <p>Commission illustrators, 3D modelers, animators, and concept artists for your project.</p>
              <span className="portal-tile-action">Book Creative Talent →</span>
            </Link>

            <Link to="/hire-developer" className="portal-tile">
              <span className="portal-tile-icon">💻</span>
              <h3>Hire a Developer</h3>
              <p>Partner with full-stack, game, WebGL, and interactive software engineers.</p>
              <span className="portal-tile-action">Request Engineering →</span>
            </Link>

            <a
              href="https://thrillartsauction.com"
              target="_blank"
              rel="noopener noreferrer"
              className="portal-tile"
            >
              <span className="portal-tile-icon">🔨</span>
              <h3>Live Art Auctions</h3>
              <p>Bid on exclusive digital and physical artworks on the official ThrillArts Auction house.</p>
              <span className="portal-tile-action">Enter Auction House ↗</span>
            </a>

            <Link to="/metaverse-rental" className="portal-tile">
              <span className="portal-tile-icon">🌐</span>
              <h3>Metaverse Rental</h3>
              <p>Host virtual events, gallery exhibitions, and brand activations in custom 3D venues.</p>
              <span className="portal-tile-action">Reserve Virtual Space →</span>
            </Link>
          </div>
        </section>
      </main>

      {/* Organized Multi-Column Footer */}
      <footer className="home-footer">
        <div className="home-footer-inner">
          <div className="footer-columns">
            <div className="footer-col">
              <Link to="/" className="home-brand">
                <div className="brand-mark">T</div>
                <div className="brand-text-wrap">
                  <span className="brand-name">ThrillArts</span>
                  <span className="brand-tagline">Creative Universe</span>
                </div>
              </Link>
              <p className="footer-brand-desc">
                A unified creative powerhouse delivering 2D/3D animation, broadcast commercials, fine
                art, interactive games, and next-gen digital experiences.
              </p>
            </div>

            <div className="footer-col">
              <h4>Production & Media</h4>
              <ul>
                <li><Link to="/animation">Animation Services</Link></li>
                <li><Link to="/animation-gallery">Animation Gallery</Link></li>
                <li><Link to="/animation-quote">Request Animation Quote</Link></li>
                <li><Link to="/products">Products & 3D Assets</Link></li>
                <li><a href="https://thrillartsMusic.com">ThrillArts Music</a></li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>Art & Experiences</h4>
              <ul>
                <li><Link to="/paintings">Paintings & Fine Art</Link></li>
                <li><Link to="/games">Games</Link></li>
                <li><Link to="/fashion">Fashion</Link></li>
                <li><Link to="/virtual-tours">Virtual Tours</Link></li>
                <li><Link to="/kids">Kids Zone</Link></li>
                <li><a href="https://thrillartsauction.com" target="_blank" rel="noopener noreferrer">Art Auction</a></li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>Business & Support</h4>
              <ul>
                <li><Link to="/hire-artist">Hire an Artist</Link></li>
                <li><Link to="/hire-developer">Hire a Developer</Link></li>
                <li><Link to="/metaverse-rental">Metaverse Rental</Link></li>
                <li><Link to="/franchise">Franchise</Link></li>
                <li><Link to="/sponsorship">Sponsorship</Link></li>
                <li><Link to="/support">Support</Link></li>
                <li><Link to="/contact-us">Contact Us</Link></li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom-bar">
            <span>© {new Date().getFullYear()} ThrillArts. All rights reserved.</span>
            <div className="footer-bottom-links">
              <Link to="/privacy-policy">Privacy Policy</Link>
              <Link to="/contact-us">Contact</Link>
              <Link to="/support">Help & Support</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Home;
